/**
 * Account Page
 * AD-20251113-SUPABASE-AUTH-VITE: Updated to use useAuth hook and proper auth guard
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, FileText, CreditCard, Calendar, Loader2, Mail } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { fireEvent } from '@/lib/analytics';
import { formatLeasePrice } from '@/lib/leaseUtils';
import { AnimatedGradientText } from '@/components/ui/animated-gradient-text';
import {
  getBillingStatusMessage,
  getBillingStatusSeverity,
  GRACE_FAILED_THRESHOLD,
} from '@/lib/billingPolicy';
import ZohoBillingButton from '@/components/ZohoBillingButton';
import ZohoBookingButton from '@/components/ZohoBookingButton';
import { getStateLabel, getStateBadgeVariant } from '@/lib/orders/transitions';
// OrderState is a string literal type - using string for now
type OrderState = string;
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, AlertTriangle, AlertCircle, XCircle, DollarSign, X, CheckCircle } from 'lucide-react';
import EarlyPayoffGuide from '@/components/EarlyPayoffGuide';
import OverviewTab from '@/components/account/tabs/OverviewTab';
import SubscriptionsTab from '@/components/account/tabs/SubscriptionsTab';
import PurchaseHistoryTab from '@/components/account/tabs/PurchaseHistoryTab';
import BillingTab from '@/components/account/tabs/BillingTab';
import DocumentsTab from '@/components/account/tabs/DocumentsTab';
import SupportTab from '@/components/account/tabs/SupportTab';
import ProfileTab from '@/components/account/tabs/ProfileTab';
import NotificationsTab from '@/components/account/tabs/NotificationsTab';

// Local, optimized parallax utilities (mirrors Home.tsx)
type Axis = 'y' | 'x' | 'both';
interface ParallaxOptions {
  speed?: number;
  axis?: Axis;
  maxTranslate?: number;
  rootMargin?: string;
}
interface ParallaxItem {
  element: HTMLElement;
  speed: number;
  axis: Axis;
  maxTranslate: number;
  inView: boolean;
  lastTransform: string;
}
const parallaxItems = new Set<ParallaxItem>();
let parallaxIo: IntersectionObserver | null = null;
let parallaxRafId: number | null = null;
let parallaxRunning = false;
let prefersReducedMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function ensureParallaxIO(rootMargin: string) {
  if (parallaxIo) return;
  parallaxIo = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const item = [...parallaxItems].find((it) => it.element === entry.target);
        if (!item) continue;
        item.inView = entry.isIntersecting;
      }
      requestParallaxTick();
    },
    { root: null, threshold: 0, rootMargin },
  );
}
function onParallaxScroll() {
  requestParallaxTick();
}
function onParallaxResize() {
  requestParallaxTick();
}
function startParallax() {
  if (parallaxRunning) return;
  parallaxRunning = true;
  window.addEventListener('scroll', onParallaxScroll, { passive: true });
  window.addEventListener('resize', onParallaxResize, { passive: true });
  requestParallaxTick();
}
function stopParallax() {
  if (!parallaxRunning) return;
  window.removeEventListener('scroll', onParallaxScroll);
  window.removeEventListener('resize', onParallaxResize);
  parallaxRunning = false;
}
function requestParallaxTick() {
  if (parallaxRafId !== null) return;
  parallaxRafId = window.requestAnimationFrame(updateParallax);
}
function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}
function updateParallax() {
  parallaxRafId = null;
  if (prefersReducedMotion) {
    parallaxItems.forEach((item) => {
      if (item.lastTransform !== '') {
        item.element.style.transform = '';
        item.lastTransform = '';
      }
    });
    return;
  }
  const viewportHeight = window.innerHeight || 0;
  const viewportWidth = window.innerWidth || 0;
  const centerY = viewportHeight / 2;
  const centerX = viewportWidth / 2;
  const farTop = -viewportHeight * 1.5;
  const farBottom = viewportHeight * 2.5;
  parallaxItems.forEach((item) => {
    const rect = item.element.getBoundingClientRect();
    if (rect.bottom < farTop || rect.top > farBottom) return;
    const elementCenterY = rect.top + rect.height / 2;
    const elementCenterX = rect.left + rect.width / 2;
    const dy = elementCenterY - centerY;
    const dx = elementCenterX - centerX;
    const translateY = clamp(-dy * item.speed, -item.maxTranslate, item.maxTranslate);
    const translateX = clamp(-dx * item.speed, -item.maxTranslate, item.maxTranslate);
    let transform = '';
    if (item.axis === 'y') {
      transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
    } else if (item.axis === 'x') {
      transform = `translate3d(${translateX.toFixed(2)}px, 0, 0)`;
    } else {
      transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0)`;
    }
    if (transform !== item.lastTransform) {
      item.element.style.transform = transform;
      item.lastTransform = transform;
    }
  });
}
function useParallaxRef(options: ParallaxOptions = {}) {
  const {
    speed = 0.06,
    axis = 'y',
    maxTranslate = 40,
    rootMargin = '20% 0px 20% 0px',
  } = options;
  const elRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = elRef.current as HTMLElement | null;
    if (!el) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      prefersReducedMotion = mql.matches;
      requestParallaxTick();
    };
    mql.addEventListener?.('change', onChange);
    ensureParallaxIO(rootMargin);
    startParallax();
    const item: ParallaxItem = {
      element: el,
      speed,
      axis,
      maxTranslate,
      inView: false,
      lastTransform: '',
    };
    try {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const vm = Math.max(0, Math.min(0.5, parseFloat(rootMargin.split(' ')[0]) / 100 || 0.2));
      const extendedTop = -vh * vm;
      const extendedBottom = vh * (1 + vm);
      item.inView = rect.bottom >= extendedTop && rect.top <= extendedBottom;
    } catch {
      item.inView = true;
    }
    parallaxItems.add(item);
    parallaxIo?.observe(el);
    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    el.style.transformStyle = 'preserve-3d';
    updateParallax();
    return () => {
      mql.removeEventListener?.('change', onChange);
      parallaxIo?.unobserve(el);
      parallaxItems.delete(item);
      el.style.willChange = '';
      el.style.backfaceVisibility = '';
      el.style.transformStyle = '';
      el.style.transform = '';
      if (parallaxItems.size === 0) {
        stopParallax();
      }
    };
  }, [axis, maxTranslate, rootMargin, speed]);
  return elRef;
}

interface User {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  address: string | null;
}

interface UserAsset {
  id: string;
  domain: string;
  relationship: 'LEASE' | 'BIN' | 'LEAD';
  status: string;
  billing_status: string;
  failed_count: number;
  zoho_invoice_customer_id: string | null;
  zoho_sign_document_id: string | null;
  created_at: string;
}

interface LeaseCancellation {
  id: string;
  status: string;
  reason: string | null;
  notes: string | null;
  requested_at: string;
}

export default function Account() {
  const { user: authUser, loading: authLoading, session } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Parallax refs
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 70 });
  const heroSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const [activeTab, setActiveTab] = useState<string>(() => {
    const tabParam = searchParams.get('tab');
    // Redirect old "domains" tab to "purchases" since domains are shown there
    if (tabParam === 'domains') {
      return 'purchases';
    }
    return tabParam || 'overview';
  });

  // Redirect old "domains" tab URLs to "purchases"
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'domains') {
      const params = new URLSearchParams(window.location.search);
      params.set('tab', 'purchases');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
      setActiveTab('purchases');
    }
  }, [searchParams]);

  // Update URL when tab changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (activeTab !== 'overview') {
      params.set('tab', activeTab);
    } else {
      params.delete('tab');
    }
    const newUrl = params.toString() 
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [activeTab]);
  
  const [user, setUser] = useState<User | null>(null);
  const [assets, setAssets] = useState<UserAsset[]>([]);
  const [cancellations, setCancellations] = useState<Record<string, LeaseCancellation>>({});
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [saving, setSaving] = useState(false);
  const [cancelDialog, setCancelDialog] = useState<{ open: boolean; asset: UserAsset | null }>({
    open: false,
    asset: null,
  });
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [earlyPayoffGuideOpen, setEarlyPayoffGuideOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showVerifyBanner, setShowVerifyBanner] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [resendVerificationLoading, setResendVerificationLoading] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);

  // AD-20251116-AUTH-Redirect-After-Create: Parse query params for fresh account + verify-pending context
  const isWelcome = searchParams.get('welcome') === 'true';
  const isVerifyPending = searchParams.get('verify_pending') === '1';
  const isVerifiedParam = searchParams.get('verified') === '1';
  const pendingEmail = searchParams.get('email') || authUser?.email || null;

  // Centralized email verification status for gating sensitive actions
  const isEmailVerified = !!authUser?.email_confirmed_at && !needsEmailVerification;

  // Handle URL params (independent of auth state so banners can show even when auth is pending)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let shouldCleanUrl = false;

    // Handle welcome banner
    if (params.get('welcome') === 'true') {
      setShowWelcome(true);
      shouldCleanUrl = true;
    }

    // Legacy: Check for welcome banner params (old system)
    if (params.get('onboard') === '1' && params.get('purchased') === 'true') {
      setShowWelcome(true);
      shouldCleanUrl = true;
    }

    // Check for onboarding view
    if (params.get('onboard') === '1') {
      setShowOnboarding(true);
      fireEvent('onboarding_opened', {});
      shouldCleanUrl = true;
    }

    // Handle verified param - show success toast
    if (params.get('verified') === '1') {
      toast({
        title: 'Email verified',
        description: "You're all set. DNS and account actions are now unlocked.",
      });
      shouldCleanUrl = true;
    }

    // Clean up URL params after processing
    if (shouldCleanUrl) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    // Check for tab param in URL
    const tabParam = params.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Data loading useEffect - only runs if authenticated
  useEffect(() => {
    if (!authUser) {
      // Still check verification status for verify-pending view
      checkEmailVerification();
      return; // Don't load data if not authenticated
    }
    
    loadData();
    loadOrders();

    // Check email verification status
    checkEmailVerification();
    
    // Auto-reconcile pending sessions (AD-20251113-S3D)
    reconcilePendingSessions();
  }, [authUser, isVerifyPending]);

  const checkEmailVerification = () => {
    if (!authUser) {
      // If no auth user but verify_pending is set, show banner
      if (isVerifyPending) {
        setShowVerifyBanner(true);
      }
      return;
    }
    
    // Check if email is verified (Supabase Auth)
    const isUnverified = !authUser.email_confirmed_at;
    setEmailUnverified(isUnverified);
    // Show verify banner if unverified OR if verify_pending param is set (fresh account)
    setShowVerifyBanner(isUnverified || isVerifyPending);
    
    // Check for email_unverified flag (AD-20251113-S3E) - legacy
    const userMetadata = authUser.user_metadata || {};
    const emailUnverifiedFlag = userMetadata.email_unverified === true;
    
    // Check for needs_email_verification flag (AD-20251113-S3D) - legacy
    setNeedsEmailVerification(userMetadata.needs_email_verification === true || emailUnverifiedFlag);
  };
  
  const reconcilePendingSessions = async () => {
    if (!session?.user || !session.access_token) return;
    
    try {
      
      const userMetadata = session.user.user_metadata || {};
      const pendingSessions = (userMetadata.pending_sessions || []) as any[];
      const pending = pendingSessions.filter((ps: any) => ps.status === 'pending');
      
      if (pending.length === 0) return;
      
      // Throttle: reconcile one at a time with delay
      for (let i = 0; i < pending.length; i++) {
        const ps = pending[i];
        if (i > 0) {
          // Wait 1 second between reconciles
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        try {
          const reconcileResponse = await fetch('/api/manage?action=link.reconcile', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ sessionId: ps.sessionId }),
          });
          
          const reconcileData = await reconcileResponse.json();
          if (reconcileData.ok && reconcileData.data?.linked) {
            // Order linked successfully - reload orders
            loadOrders();
            
            // Check if needs_email_verification flag was set
            if (reconcileData.data?.needsEmailVerification) {
              setNeedsEmailVerification(true);
            }
          }
        } catch (reconcileError) {
          // Non-blocking - continue with next session
          console.error('[account] Reconcile error (non-blocking):', reconcileError);
        }
      }
    } catch (error) {
      console.error('[account] Reconcile pending sessions error:', error);
    }
  };

  const handleResendVerification = async () => {
    // AD-20251116-AUTH-Redirect-After-Create: Support resend for pending email from query params
    const targetEmail = authUser?.email || pendingEmail;
    if (!targetEmail) return;

    setResendVerificationLoading(true);
    try {
      // Use Supabase Auth's built-in resend method
      // Ensure verification links use the same email-confirmed redirect as initial signup
      const emailRedirectTo =
        import.meta.env.DEV
          ? 'http://localhost:8081/account/email-confirmed'
          : 'https://attaindomains.com/account/email-confirmed';

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: targetEmail,
        options: {
          emailRedirectTo,
        },
      });

      if (error) {
        throw error;
      }

      fireEvent('email_verification_sent', {
        user_id: authUser?.id?.substring(0, 8) + '***' || 'pending',
      });
      
      setVerificationSent(true);
      toast({
        title: 'Verification email sent',
        description: 'Please check your email and click the verification link.',
      });
      
      // Hide the sent message after 5 seconds
      setTimeout(() => setVerificationSent(false), 5000);
    } catch (error) {
      console.error('[account] Resend verification error:', error);
      toast({
        title: 'Failed to send verification email',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setResendVerificationLoading(false);
    }
  };

  const loadData = async () => {
    try {
      // Ensure we have a session before calling Edge Functions
      if (!session?.access_token) {
        console.error('[account] No session token available');
        toast({
          title: 'Session expired',
          description: 'Please sign in again.',
          variant: 'destructive',
        });
        await supabase.auth.signOut();
        navigate('/account/login');
        return;
      }

      // Get user profile - explicitly pass Authorization header
      const { data: userData, error: userError } =
        await supabase.functions.invoke('account-profile', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
      
      if (userError) {
        // Detailed error logging for debugging
        console.error('[account] Profile load error details:', {
          message: userError.message,
          status: userError.status,
          statusCode: userError.statusCode,
          context: userError.context,
          fullError: userError,
        });
        
        // Check if it's an auth error (401) - only redirect to login for auth failures
        const isAuthError = userError.message?.includes('Unauthorized') || 
                           userError.status === 401 ||
                           userError.statusCode === 401 ||
                           userError.message?.includes('401');
        
        if (isAuthError) {
          console.error('[account] Auth error loading profile - signing out');
          await supabase.auth.signOut();
          navigate('/account/login');
          return;
        }
        
        // Parse error message for more specific user-facing messages
        let errorTitle = 'Failed to load profile';
        let errorDescription = userError.message || 'Please try refreshing the page.';
        
        // Check for specific error types
        if (userError.message?.includes('No rows returned') || 
            userError.message?.includes('PGRST116') ||
            userError.status === 404 ||
            userError.statusCode === 404) {
          errorTitle = 'Profile not found';
          errorDescription = 'Your account profile could not be found. Please contact support.';
        } else if (userError.status === 500 || userError.statusCode === 500) {
          errorTitle = 'Server error';
          errorDescription = 'A server error occurred. Please try again in a moment.';
        } else if (userError.message?.includes('timeout') || userError.message?.includes('network')) {
          errorTitle = 'Connection error';
          errorDescription = 'Unable to connect to the server. Please check your internet connection.';
        }
        
        toast({
          title: errorTitle,
          description: errorDescription,
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      setUser(userData);
      setName(userData.name || '');
      setPhone(userData.phone || '');
      setAddress(userData.address || '');

      // Get user assets - explicitly pass Authorization header
      const { data: assetsData, error: assetsError } =
        await supabase.functions.invoke('account-assets', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
      
      if (assetsError) {
        // Detailed error logging for debugging
        console.error('[account] Assets load error details:', {
          message: assetsError.message,
          status: assetsError.status,
          statusCode: assetsError.statusCode,
          context: assetsError.context,
          fullError: assetsError,
        });
        
        // Check if it's an auth error (401)
        const isAuthError = assetsError.message?.includes('Unauthorized') || 
                           assetsError.status === 401 ||
                           assetsError.statusCode === 401 ||
                           assetsError.message?.includes('401');
        
        if (isAuthError) {
          console.error('[account] Auth error loading assets - signing out');
          await supabase.auth.signOut();
          navigate('/account/login');
          return;
        }
        
        // Parse error message for more specific user-facing messages
        let errorTitle = 'Failed to load domains';
        let errorDescription = assetsError.message || 'Some data may be missing.';
        
        if (assetsError.status === 404 || assetsError.statusCode === 404) {
          errorTitle = 'Domains not found';
          errorDescription = 'No domains found for your account.';
        } else if (assetsError.status === 500 || assetsError.statusCode === 500) {
          errorTitle = 'Server error';
          errorDescription = 'A server error occurred while loading your domains. Please try again.';
        } else if (assetsError.message?.includes('timeout') || assetsError.message?.includes('network')) {
          errorTitle = 'Connection error';
          errorDescription = 'Unable to load domains. Please check your internet connection.';
        }
        
        toast({
          title: errorTitle,
          description: errorDescription,
          variant: 'destructive',
        });
      } else {
        // Deduplicate assets by domain + relationship to prevent UI duplicates
        const assetsMap = new Map<string, UserAsset>();
        (assetsData || []).forEach((asset: UserAsset) => {
          const key = `${asset.domain}-${asset.relationship}`;
          // Keep the most recent asset if duplicates exist
          if (!assetsMap.has(key) || (asset.created_at && assetsMap.get(key)?.created_at && 
              new Date(asset.created_at) > new Date(assetsMap.get(key)!.created_at))) {
            assetsMap.set(key, asset);
          }
        });
        const deduplicatedAssets = Array.from(assetsMap.values());
        setAssets(deduplicatedAssets);
      }

      // Load cancellations for lease assets (use current assets state)
      const currentAssets = assetsData || [];
      const leaseAssetIds = currentAssets
        .filter((a: UserAsset) => a.relationship === 'LEASE')
        .map((a: UserAsset) => a.id);
      if (leaseAssetIds.length > 0) {
        const { data: cancelData } = await supabase
          .from('lease_cancellations')
          .select('*')
          .in('asset_id', leaseAssetIds)
          .in('status', ['pending', 'approved', 'denied']);

        if (cancelData) {
          const cancelMap: Record<string, LeaseCancellation> = {};
          cancelData.forEach((c: any) => {
            cancelMap[c.asset_id] = c;
          });
          setCancellations(cancelMap);
        }
      }
    } catch (error: any) {
      // Catch any unexpected errors
      console.error('[account] Unexpected error:', {
        message: error?.message,
        status: error?.status,
        statusCode: error?.statusCode,
        stack: error?.stack,
        fullError: error,
      });
      
      // Check if it's an auth error
      const isAuthError = error?.message?.includes('Unauthorized') || 
                         error?.status === 401 ||
                         error?.statusCode === 401;
      
      if (isAuthError) {
        await supabase.auth.signOut();
        navigate('/account/login');
        return;
      }
      
      // Parse error for user-friendly message
      let errorTitle = 'Error loading account data';
      let errorDescription = error?.message || 'Please try refreshing the page.';
      
      if (error?.status === 500 || error?.statusCode === 500) {
        errorTitle = 'Server error';
        errorDescription = 'A server error occurred. Please try again in a moment.';
      } else if (error?.message?.includes('timeout') || error?.message?.includes('network')) {
        errorTitle = 'Connection error';
        errorDescription = 'Unable to connect to the server. Please check your internet connection.';
      }
      
      toast({
        title: errorTitle,
        description: errorDescription,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      setOrdersLoading(true);

      // Fetch orders via direct query (RLS will filter to current user)
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (ordersError) {
        console.error('[account] Orders error:', ordersError);
        setOrders([]);
      } else {
        setOrders(ordersData || []);
        fireEvent('account_view', { orders_count: ordersData?.length || 0 });
      }

      // Fetch stripe_customer_id from purchases table (non-blocking)
      if (authUser?.id) {
        try {
          const { data: purchaseData, error: purchaseError } = await supabase
            .from('purchases')
            .select('stripe_customer_id')
            .eq('user_id', authUser.id)
            .not('stripe_customer_id', 'is', null)
            .limit(1)
            .maybeSingle();

          if (purchaseError) {
            // Non-critical - just log and continue
            console.debug('[account] Could not fetch stripe_customer_id:', purchaseError);
          } else if (purchaseData?.stripe_customer_id) {
            setStripeCustomerId(purchaseData.stripe_customer_id);
          }
        } catch (purchaseError) {
          // Non-critical - just log and continue
          console.debug('[account] Could not fetch stripe_customer_id:', purchaseError);
        }
      }
    } catch (error) {
      console.error('[account] Load orders error:', error);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (!session?.access_token) {
        throw new Error('Missing session token');
      }

      const { error } = await supabase.functions.invoke('account-profile', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: { name, phone, address },
      });

      if (error) throw error;

      fireEvent('profile_saved', {});
      toast({ title: 'Profile updated', description: 'Your changes have been saved.' });

      setUser((prev) => (prev ? { ...prev, name, phone, address } : null));
    } catch (error) {
      console.error('[account] Save error:', error);
      toast({
        title: 'Failed to save',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelLease = async () => {
    if (!cancelDialog.asset) return;

    setCancelLoading(true);
    try {
      const { error } = await supabase.functions.invoke('lease-cancel-request', {
        body: { asset_id: cancelDialog.asset.id, reason: cancelReason },
      });

      if (error) throw error;

      fireEvent('lease_cancel_requested', {
        asset_id: cancelDialog.asset.id,
        domain: cancelDialog.asset.domain,
      });
      toast({
        title: 'Cancellation requested',
        description: 'Your request has been submitted for review.',
      });

      setCancelDialog({ open: false, asset: null });
      setCancelReason('');
      await loadData();
    } catch (error) {
      console.error('[account] Cancel lease error:', error);
      toast({
        title: 'Failed to request cancellation',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const handleWithdrawCancellation = async (assetId: string) => {
    try {
      const { error } = await supabase.functions.invoke('lease-cancel-withdraw', {
        body: { asset_id: assetId },
      });

      if (error) throw error;

      fireEvent('lease_cancel_withdrawn', { asset_id: assetId });
      toast({
        title: 'Request withdrawn',
        description: 'Your cancellation request has been withdrawn.',
      });

      await loadData();
    } catch (error) {
      console.error('[account] Withdraw error:', error);
      toast({
        title: 'Failed to withdraw',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // AD-20251116-AUTH-Redirect-After-Create: Handle unauthenticated state
  // Special case: verify-pending allows limited view without auth
  if (!authLoading && !authUser && !isVerifyPending) {
    // Normal unauthenticated - redirect to login
    navigate('/account/login?redirect=/account', { replace: true });
    return null;
  }

  // Show loading state while checking auth or loading data
  // Note: For verify-pending without auth, we'll show a limited view below
  if (authLoading || (loading && authUser)) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Verify-pending view without auth (fresh account, email not confirmed yet)
  if (!authUser && !authLoading && isVerifyPending) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>Verify your email to complete setup</CardTitle>
                <CardDescription>
                  Your account was created. Check {pendingEmail || 'your email'} to verify your email address.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-blue-50 border-blue-200">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <p className="mb-3">
                      <strong>Your account was created.</strong> Check {pendingEmail || 'your email'} to verify your email so we can send DNS and ownership updates.
                    </p>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleResendVerification}
                        disabled={resendVerificationLoading || !pendingEmail}
                        className="border-blue-300"
                      >
                        {resendVerificationLoading ? 'Sending...' : 'Resend verification email'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate('/support')}
                        className="text-sm"
                      >
                        Contact Support
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
                {verificationSent && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      Verification email sent! Check your inbox.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Check if session was created via admin magic link (test banner)
  const showTestBanner =
    typeof window !== 'undefined' &&
    (window.location.search.includes('token=') || sessionStorage.getItem('admin_test_session'));

  if (showTestBanner && typeof window !== 'undefined') {
    sessionStorage.setItem('admin_test_session', 'true');
  }

  return (
    <div className="min-h-screen bg-background">
      {showTestBanner && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/20 py-2">
          <div className="container mx-auto px-4">
            <p className="text-sm text-center text-yellow-700 dark:text-yellow-400">
              🧪 Test session — not a real customer
            </p>
          </div>
        </div>
      )}
      {/* AD-20251116-AUTH-Redirect-After-Create: Enhanced Email Verification Banner */}
      {showVerifyBanner && !verificationSent && (
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <Mail className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="mb-2">
                  <strong>Your account was created.</strong> Check <strong>{pendingEmail || authUser?.email || 'your email'}</strong> to verify your email so we can send DNS and ownership updates.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleResendVerification}
                    disabled={resendVerificationLoading}
                    className="border-blue-300"
                  >
                    {resendVerificationLoading ? 'Sending...' : 'Resend verification email'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setActiveTab('profile');
                      navigate('/account?tab=profile', { replace: true });
                    }}
                    className="text-sm text-blue-700 hover:text-blue-900"
                  >
                    Change email in Profile
                  </Button>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowVerifyBanner(false)}
                className="flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {verificationSent && (
        <Alert className="mb-6 bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Verification email sent! Check your inbox.
          </AlertDescription>
        </Alert>
      )}

      {/* Legacy Email Verification Banner (for backward compatibility) */}
      {(emailUnverified || needsEmailVerification) && !showVerifyBanner && (
        <div className="bg-blue-50 border-b border-blue-200 py-3">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-600" />
                <p className="text-sm text-blue-900">
                  {needsEmailVerification 
                    ? 'The email used for payment differs from your account email. Please verify your email address.'
                    : 'Please verify your email address to secure your account.'}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleResendVerification}
                disabled={resendVerificationLoading}
              >
                {resendVerificationLoading ? 'Sending...' : 'Resend Verification'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="min-h-screen bg-background"
      >
        {/* Centered Header Section */}
        <section className="relative py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto text-center">
              <h1
                ref={heroTitleRef as any}
                className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
              >
                <AnimatedGradientText colorFrom="#0000FF" colorTo="#000000" className="text-6xl md:text-7xl font-bold">
                  Account
                </AnimatedGradientText>
              </h1>
              <p
                ref={heroSubRef as any}
                className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed mb-12 font-normal"
              >
                Manage everything in one place
              </p>
            </div>
          </div>
        </section>

        {/* Welcome Banner for New Purchases */}
        {showWelcome && (
          <div className="max-w-5xl mx-auto px-4 md:px-6 mb-6">
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Welcome to Attain Domains!</strong> Your purchase was successful and your account is ready to go.
                {new URLSearchParams(window.location.search).get('first_purchase') && ' Check your email for your order confirmation.'}
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Onboarding Panels (AD-20251113-S4) */}
        {showOnboarding && (
          <div className="max-w-5xl mx-auto px-4 md:px-6 mb-6 space-y-4">
              {/* BIN Onboarding Panel */}
              {assets.some((a) => a.relationship === 'BIN') && (
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      Transfer Checklist
                    </CardTitle>
                    <CardDescription>Complete these steps to transfer your domain</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-blue-900">1.</span>
                        <div>
                          <p className="text-sm font-medium">Wait for EPP Code</p>
                          <p className="text-xs text-muted-foreground">
                            We'll email your EPP (authorization) code within 24 hours of purchase.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-blue-900">2.</span>
                        <div>
                          <p className="text-sm font-medium">Initiate Transfer at Your Registrar</p>
                          <p className="text-xs text-muted-foreground">
                            Use the EPP code to start the transfer process at your domain registrar.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-blue-900">3.</span>
                        <div>
                          <p className="text-sm font-medium">Approve Transfer</p>
                          <p className="text-xs text-muted-foreground">
                            Approve the transfer request when prompted by your registrar.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.open('https://attaindomains.com/help', '_blank');
                        }}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Help Guide
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* LTO Onboarding Panel */}
              {assets.some((a) => a.relationship === 'LEASE') && (
                <Card className="border-purple-200 bg-purple-50/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-purple-600" />
                      Lease-to-Own Setup
                    </CardTitle>
                    <CardDescription>Your domain is ready to use right away</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold mb-1">✅ Use DNS Now</p>
                        <p className="text-xs text-muted-foreground">
                          Your domain DNS is active. You can start using it for hosting, email, or websites immediately.
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold mb-1">💳 Manage Billing</p>
                        <p className="text-xs text-muted-foreground mb-2">
                          Update payment methods, view invoices, and manage your subscription.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            fireEvent('portal_clicked', { origin: 'onboarding' });
                            const customerId = stripeCustomerId || new URLSearchParams(window.location.search).get('cid');
                            if (customerId && customerId.startsWith('cus_')) {
                              try {
                                const response = await fetch('/api/stripe/portal', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ customerId }),
                                });
                                const data = await response.json();
                                if (data.ok && data.data?.url) {
                                  window.location.href = data.data.url;
                                } else {
                                  throw new Error(data.message || 'Failed to create portal session');
                                }
                              } catch (error) {
                                console.error('[account] Billing portal error:', error);
                                toast({
                                  title: 'Error',
                                  description: 'Could not open billing portal. Please try again or contact support.',
                                  variant: 'destructive',
                                });
                              }
                            } else {
                              toast({
                                title: 'Billing portal',
                                description: 'Visit the Billing tab to access your payment portal.',
                              });
                            }
                          }}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Open Stripe Portal
                        </Button>
                      </div>
                      <div>
                        <p className="text-sm font-semibold mb-1">💰 Early Payoff</p>
                        <p className="text-xs text-muted-foreground mb-2">
                          Pay off your remaining balance early and own the domain immediately.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            fireEvent('early_payoff_clicked', { origin: 'onboarding' });
                            setEarlyPayoffGuideOpen(true);
                          }}
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Learn About Early Payoff
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
          </div>
        )}

        {/* Tabs Section */}
        <div className="max-w-5xl mx-auto px-4 md:px-6 pb-24">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mt-4 flex flex-col md:flex-row flex-wrap justify-center gap-2 md:gap-4 bg-transparent p-0 h-auto">
              <TabsTrigger 
                value="overview" 
                className="px-4 py-2 text-base font-medium transition-colors text-foreground data-[state=active]:text-[#0000FF] data-[state=inactive]:hover:text-[#0000FF] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0000FF]"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger 
                value="profile"
                className="px-4 py-2 text-base font-medium transition-colors text-foreground data-[state=active]:text-[#0000FF] data-[state=inactive]:hover:text-[#0000FF] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0000FF]"
              >
                Profile
              </TabsTrigger>
              <TabsTrigger 
                value="subscriptions"
                className="px-4 py-2 text-base font-medium transition-colors text-foreground data-[state=active]:text-[#0000FF] data-[state=inactive]:hover:text-[#0000FF] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0000FF]"
              >
                Leases
              </TabsTrigger>
              <TabsTrigger 
                value="purchases"
                className="px-4 py-2 text-base font-medium transition-colors text-foreground data-[state=active]:text-[#0000FF] data-[state=inactive]:hover:text-[#0000FF] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0000FF]"
              >
                Purchased
              </TabsTrigger>
              <TabsTrigger 
                value="billing"
                className="px-4 py-2 text-base font-medium transition-colors text-foreground data-[state=active]:text-[#0000FF] data-[state=inactive]:hover:text-[#0000FF] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0000FF]"
              >
                Billing
              </TabsTrigger>
              <TabsTrigger 
                value="documents"
                className="px-4 py-2 text-base font-medium transition-colors text-foreground data-[state=active]:text-[#0000FF] data-[state=inactive]:hover:text-[#0000FF] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0000FF]"
              >
                Documents
              </TabsTrigger>
              <TabsTrigger 
                value="support"
                className="px-4 py-2 text-base font-medium transition-colors text-foreground data-[state=active]:text-[#0000FF] data-[state=inactive]:hover:text-[#0000FF] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0000FF]"
              >
                Support
              </TabsTrigger>
              <TabsTrigger 
                value="notifications"
                className="px-4 py-2 text-base font-medium transition-colors text-foreground data-[state=active]:text-[#0000FF] data-[state=inactive]:hover:text-[#0000FF] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0000FF]"
              >
                Services
              </TabsTrigger>
            </TabsList>

            <div className="max-w-5xl mx-auto mt-8 px-4 md:px-0">
              <TabsContent value="overview" className="mt-0" key="overview">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  <div className="apple-card p-6 md:p-8 space-y-4">
                    <OverviewTab
                      assetsCount={assets.length}
                      nextPaymentDate={null}
                      openTicketsCount={0}
                    />
                  </div>
                </motion.div>
              </TabsContent>

              <TabsContent value="subscriptions" className="mt-0" key="subscriptions">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  <div className="apple-card p-6 md:p-8 space-y-4">
                    <SubscriptionsTab />
                  </div>
                </motion.div>
              </TabsContent>

              <TabsContent value="purchases" className="mt-0" key="purchases">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  <div className="apple-card p-6 md:p-8 space-y-4">
                    <PurchaseHistoryTab orders={orders} ordersLoading={ordersLoading} />
                  </div>
                </motion.div>
              </TabsContent>

              <TabsContent value="billing" className="mt-0" key="billing">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  <div className="apple-card p-6 md:p-8 space-y-4">
                    <BillingTab
                      stripeCustomerId={stripeCustomerId}
                      assets={assets}
                      userEmail={user?.email}
                      isEmailVerified={isEmailVerified}
                      onEarlyPayoffClick={() => {
                        fireEvent('early_payoff_clicked', { origin: 'account' });
                        setEarlyPayoffGuideOpen(true);
                      }}
                    />
                  </div>
                </motion.div>
              </TabsContent>

              <TabsContent value="documents" className="mt-0" key="documents">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  <div className="apple-card p-6 md:p-8 space-y-4">
                    <DocumentsTab />
                  </div>
                </motion.div>
              </TabsContent>

              <TabsContent value="support" className="mt-0" key="support">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  <div className="apple-card p-6 md:p-8 space-y-4">
                    <SupportTab />
                  </div>
                </motion.div>
              </TabsContent>

              <TabsContent value="profile" className="mt-0" key="profile">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  <ProfileTab
                    userEmail={user?.email || ''}
                    name={name}
                    phone={phone}
                    address={address}
                    saving={saving}
                    onNameChange={setName}
                    onPhoneChange={setPhone}
                    onAddressChange={setAddress}
                    onSave={handleSaveProfile}
                  />
                </motion.div>
              </TabsContent>

              <TabsContent value="notifications" className="mt-0" key="notifications">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  <div className="apple-card p-6 md:p-8 space-y-4">
                    <NotificationsTab />
                  </div>
                </motion.div>
              </TabsContent>
            </div>

          </Tabs>
        </div>
      </motion.div>

      <Dialog
          open={cancelDialog.open}
          onOpenChange={(open) => setCancelDialog({ ...cancelDialog, open })}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Lease for {cancelDialog.asset?.domain}</DialogTitle>
              <DialogDescription>
                You can still purchase this domain at the BIN price at any time. Your request will
                be reviewed by our team.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="cancel-reason">Reason (optional)</Label>
              <Textarea
                id="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Tell us why you're canceling..."
                className="mt-2"
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setCancelDialog({ open: false, asset: null });
                  setCancelReason('');
                }}
              >
                Keep Lease
              </Button>
              <Button onClick={handleCancelLease} disabled={cancelLoading} variant="destructive">
                Request Cancellation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Early Payoff Guide Modal (P20251023-UI-P5E) */}
        <EarlyPayoffGuide
          open={earlyPayoffGuideOpen}
          onOpenChange={setEarlyPayoffGuideOpen}
          onOpenPortal={async () => {
            const customerId = stripeCustomerId || new URLSearchParams(window.location.search).get('cid');
            if (customerId && customerId.startsWith('cus_')) {
              try {
                const response = await fetch('/api/stripe/portal', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ customerId }),
                });
                const data = await response.json();
                if (data.ok && data.data?.url) {
                  window.location.href = data.data.url;
                } else {
                  throw new Error(data.message || 'Failed to create portal session');
                }
              } catch (error) {
                console.error('[account] Billing portal error:', error);
                toast({
                  title: 'Error',
                  description: 'Could not open billing portal. Please try again or contact support.',
                  variant: 'destructive',
                });
              }
            } else {
              toast({
                title: 'No billing account',
                description: 'Complete a purchase first to access billing portal.',
              });
            }
            setEarlyPayoffGuideOpen(false);
          }}
        />
    </div>
  );
}
